import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

async function fetchQuoteByEventId(eventId: string) {
  const { data, error } = await supabase
    .from("quotes")
    .select("*, payments(*)")
    .eq("event_id", eventId)
    .single();

  if (error && error.code !== "PGRST116") throw error;
  return data;
}

async function createQuote(quote: InsertTables<"quotes">) {
  const { data, error } = await supabase
    .from("quotes")
    .insert([quote])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateQuote(id: string, updates: UpdateTables<"quotes">) {
  const { data, error } = await supabase
    .from("quotes")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export function useQuote(eventId: string) {
  return useQuery({
    queryKey: ["quote", eventId],
    queryFn: () => fetchQuoteByEventId(eventId),
    enabled: !!eventId,
  });
}

export function useCreateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createQuote,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["quote", data.event_id] });
      queryClient.invalidateQueries({ queryKey: ["event", data.event_id] });
    },
  });
}

export function useUpdateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"quotes">;
    }) => updateQuote(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["quote", data.event_id] });
      queryClient.invalidateQueries({ queryKey: ["event", data.event_id] });
    },
  });
}

export type Quote = Tables<"quotes">;
